
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, Loader2, UserPlus } from "lucide-react";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../../api";
import { UserFilters } from "./UserFilters";
import { UserTableView } from "./UserTableView";
import { UserSideView } from "./UserSideView";
import { AddUserDialog } from "../AddUserDialog";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useDeleteUser } from "@/hooks/useDeleteUser";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { toConvexId } from "@/types/convex";
import { useToast } from "@/hooks/use-toast";

export interface EnhancedUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  device_management_type: string;
  is_device_owner: boolean;
  device_count: number;
  managed_users_count: number;
  management_roles: Array<{ role: string }>;
  phone?: string;
  created_at: string;
  parent?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

export const EnhancedUsersSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [managementTypeFilter, setManagementTypeFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<EnhancedUser | null>(null);
  const [isUserSideViewOpen, setIsUserSideViewOpen] = useState(false);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<EnhancedUser | null>(null);
  const [fetchDevicesForUserId, setFetchDevicesForUserId] = useState<string | null>(null);
  
  // Pagination state
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);
  const [currentPage, setCurrentPage] = useState(1);

  const { deleteUser, isDeleting } = useDeleteUser();
  const { toast } = useToast();

  // Fetch devices for the user being deleted
  const userDevicesData = useConvexQuery(
    api.users.get.byConvexId,
    fetchDevicesForUserId ? { userId: toConvexId.user(fetchDevicesForUserId) } : "skip"
  );

  const userDevices = userDevicesData || [];

  // Check admin status - for now, always show the button as we need to implement proper role checking
  const { isSuperAdmin } = useIsAdmin();
  const shouldShowAddButton = true; // Temporarily show for all users until proper role system is implemented
  
  // Fetch users data from Convex
  const usersData = useConvexQuery(
    api.users.list.all, 
    { 
      paginationOpts: {
        cursor: cursor,
        numItems: 100
      }
    }
  );

  const goToNextPage = () => {
    if (usersData?.continueCursor && !usersData?.isDone) {
      setCursorHistory(prev => [...prev, usersData.continueCursor]);
      setCursor(usersData.continueCursor);
      setCurrentPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      const newHistory = [...cursorHistory];
      newHistory.pop();
      setCursorHistory(newHistory);
      setCursor(newHistory[newHistory.length - 1]);
      setCurrentPage(prev => prev - 1);
    }
  };

  // Log raw response from api.users.list.all
  console.log('Raw response from api.users.list.all:', usersData);

  // Transform Convex data to match our interface - using real data where available
  const users: EnhancedUser[] = usersData?.page ? usersData.page.map((user: any) => {
    // Extract parent information if available from the API
    const parentInfo = user.parent || user.managedByUser || user.manager || user.parentUser;
    
    return {
      id: user._id,
      email: user.email || '',
      first_name: user.firstName || '',
      last_name: user.lastName || '',
      status: 'active', // Default status since not available in real data
      device_management_type: user.identity || 'self-managed', // Use real identity from API
      is_device_owner: false, // Default since not available in real data
      device_count: 0, // Default since not available in real data
      managed_users_count: 0, // Default since not available in real data
      management_roles: user.roles || [], // Use actual roles if available
      phone: user.phone || '',
      created_at: user._creationTime ? new Date(user._creationTime).toISOString() : '',
      parent: parentInfo ? {
        id: parentInfo._id || parentInfo.id,
        first_name: parentInfo.firstName || parentInfo.first_name || '',
        last_name: parentInfo.lastName || parentInfo.last_name || '',
        email: parentInfo.email || ''
      } : undefined
    };
  }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) : [];

  const isLoading = usersData === undefined;

  // Handle URL parameters for user detail view
  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId && users.length > 0) {
      const user = users.find(u => u.id === userId);
      if (user) {
        setSelectedUser(user);
        setIsUserSideViewOpen(true);
      }
    }
  }, [searchParams, users]);

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === "" || 
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesManagementType = managementTypeFilter === "all" || user.device_management_type === managementTypeFilter;
    
    return matchesSearch && matchesManagementType;
  });

  // All users are displayed in a single table
  const displayUsers = filteredUsers;

  const handleAddUser = () => {
    setIsAddUserDialogOpen(true);
  };

  const handleDeleteUserClick = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    console.log('🗑️ Delete icon clicked - Raw user data:', user);
    console.log('🗑️ User devices data:', userDevicesData);

    setUserToDelete(user);
    setFetchDevicesForUserId(userId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    const success = await deleteUser(userToDelete.id);
    if (success) {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setFetchDevicesForUserId(null);
      
      // Close the side view if the deleted user was selected
      if (selectedUser?.id === userToDelete.id) {
        setSelectedUser(null);
        setIsUserSideViewOpen(false);
      }
    }
  };

  const handleUserClick = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setIsUserSideViewOpen(true);
      // Update URL with user parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('userId', userId);
      setSearchParams(newSearchParams);
    }
  };

  const handleCloseUserSideView = () => {
    setIsUserSideViewOpen(false);
    setSelectedUser(null);
    // Remove userId from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('userId');
    setSearchParams(newSearchParams);
  };

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div className="w-full transition-all duration-200 flex flex-col overflow-hidden">
          <div className="space-y-4 pt-5 px-6 pb-6 flex-1 flex flex-col overflow-hidden">
            {/* Filters skeleton */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 flex items-center gap-4">
                <div className="h-10 w-64 bg-muted animate-pulse rounded-md" />
                <div className="h-10 w-32 bg-muted animate-pulse rounded-md" />
                <div className="h-10 w-32 bg-muted animate-pulse rounded-md" />
              </div>
              {shouldShowAddButton && (
                <div className="h-10 w-28 bg-muted animate-pulse rounded-md" />
              )}
            </div>

            {/* Table skeleton */}
            <div className="border rounded-lg overflow-hidden" style={{ maxHeight: '850px' }}>
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading users...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main content area */}
      <div className={`${isUserSideViewOpen ? 'w-2/3' : 'w-full'} transition-all duration-200 flex flex-col overflow-hidden`}>
        <div className="space-y-4 pt-5 px-6 pb-6 flex-1 flex flex-col overflow-hidden">
          {/* Filters with Add User Button */}
          <UserFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            managementTypeFilter={managementTypeFilter}
            onManagementTypeChange={setManagementTypeFilter}
            users={users}
            shouldShowAddButton={shouldShowAddButton}
            onAddUser={handleAddUser}
            isUserSideViewOpen={isUserSideViewOpen}
          />

          {/* Users Table - Fixed height with scrolling */}
          <div className="border rounded-lg overflow-hidden" style={{ maxHeight: '750px' }}>
            <div className="overflow-y-auto h-full">
              {displayUsers.length === 0 && !isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm || managementTypeFilter !== "all" 
                    ? "No users match your current filters."
                    : "No users found."
                  }
                </div>
              ) : (
                <UserTableView 
                  users={displayUsers} 
                  isLoading={isLoading}
                  onUserClick={handleUserClick}
                  onDeleteUserClick={handleDeleteUserClick}
                  isDeleting={isDeleting}
                />
              )}
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-2 py-3 border rounded-lg bg-muted/30">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} • Showing {displayUsers.length} users
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={goToNextPage}
                disabled={usersData?.isDone}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* User Side View */}
      <div className={`${isUserSideViewOpen ? 'w-1/3' : 'w-0'} transition-all duration-200 overflow-hidden`}>
        <div className="sticky top-0 h-full overflow-y-auto">
          <UserSideView
            user={selectedUser}
            isOpen={isUserSideViewOpen}
            onClose={handleCloseUserSideView}
          />
        </div>
      </div>

      {/* Add User Dialog */}
      <AddUserDialog
        isOpen={isAddUserDialogOpen}
        onClose={() => setIsAddUserDialogOpen(false)}
      />

      {/* Delete User Dialog */}
      {userToDelete && (
        <DeleteUserDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          userName={`${userToDelete.first_name} ${userToDelete.last_name}`}
          devices={userDevices.map(d => ({ ...d, associatedUsers: [] as any[] }))}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};
